import React, { useState } from "react";

const CONTACT_EMAIL = "info@dingonet.org";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s()-]{7,20}$/;
const URL_REGEX = /https?:\/\//gi;

const FIELD_LIMITS = {
  fullName: 100,
  email: 254,
  phone: 30,
  subject: 150,
  message: 2000,
};

const DEFAULT_FORM_DATA = {
  fullName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const validateContactForm = (data, honeypot) => {
  const errors = {};

  if (honeypot.trim()) {
    return errors;
  }

  const fullName = data.fullName.trim();
  const email = data.email.trim();
  const phone = data.phone.trim();
  const subject = data.subject.trim();
  const message = data.message.trim();

  if (!fullName) {
    errors.fullName = "Full name is required.";
  } else if (fullName.length < 2) {
    errors.fullName = "Please enter at least 2 characters.";
  } else if (fullName.length > FIELD_LIMITS.fullName) {
    errors.fullName = `Name must be under ${FIELD_LIMITS.fullName} characters.`;
  } else if (/\d{5,}/.test(fullName) || /https?:\/\//i.test(fullName)) {
    errors.fullName = "Please enter a valid name.";
  }

  if (!email) {
    errors.email = "Email is required.";
  } else if (!EMAIL_REGEX.test(email)) {
    errors.email = "Please enter a valid email address.";
  } else if (email.length > FIELD_LIMITS.email) {
    errors.email = "Email is too long.";
  }

  if (!phone) {
    errors.phone = "Phone number is required.";
  } else if (!PHONE_REGEX.test(phone)) {
    errors.phone = "Please enter a valid phone number.";
  } else if (phone.replace(/\D/g, "").length < 7) {
    errors.phone = "Phone number must have at least 7 digits.";
  }

  if (!subject) {
    errors.subject = "Subject is required.";
  } else if (subject.length < 3) {
    errors.subject = "Subject must be at least 3 characters.";
  } else if (subject.length > FIELD_LIMITS.subject) {
    errors.subject = `Subject must be under ${FIELD_LIMITS.subject} characters.`;
  }

  if (!message) {
    errors.message = "Message is required.";
  } else if (message.length < 10) {
    errors.message = "Message must be at least 10 characters.";
  } else if (message.length > FIELD_LIMITS.message) {
    errors.message = `Message must be under ${FIELD_LIMITS.message} characters.`;
  } else if ((message.match(URL_REGEX) || []).length > 2) {
    errors.message = "Please limit links in your message.";
  }

  return errors;
};

const submitContactForm = async (data) => {
  const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY?.trim();
  if (!accessKey) {
    throw new Error(
      `The contact form is not configured yet. Please email ${CONTACT_EMAIL} directly.`
    );
  }

  const response = await fetch(WEB3FORMS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      access_key: accessKey,
      subject: `DINGONET Contact: ${data.subject.trim()}`,
      from_name: data.fullName.trim(),
      email: data.email.trim(),
      phone: data.phone.trim(),
      message: data.message.trim(),
      botcheck: false,
    }),
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || "Unable to send your message. Please try again.");
  }
};

const ContactForm = () => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateContactForm(formData, honeypot);
    setErrors(nextErrors);

    if (honeypot.trim()) {
      setSubmittedEmail(formData.email.trim());
      setIsSubmitted(true);
      return;
    }

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      await submitContactForm(formData);
      setSubmittedEmail(formData.email.trim());
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : `Something went wrong. Please try again or email ${CONTACT_EMAIL} directly.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA);
    setHoneypot("");
    setErrors({});
    setIsSubmitted(false);
    setIsSubmitting(false);
    setSubmitError("");
    setSubmittedEmail("");
  };

  const inputClass = (error) =>
    `w-full bg-white/5 border rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 outline-none transition-all duration-200 ${
      error
        ? "border-red-500/50 ring-2 ring-red-500/10"
        : "border-white/10 focus:border-[#00E5CC]/50 focus:ring-2 focus:ring-[#00E5CC]/10"
    }`;

  if (isSubmitted) {
    return (
      <div
        className="py-12 text-center sm:py-16"
        role="status"
        aria-live="polite"
      >
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#00E5CC]/15 text-3xl"
          aria-hidden
        >
          ✓
        </div>
        <h3 className="mb-3 text-2xl font-black text-white sm:text-3xl">Message sent successfully</h3>
        <p className="mx-auto mb-8 max-w-md text-sm leading-relaxed text-slate-400">
          Thank you for contacting DINGONET. Our team will review your message and respond within 2
          business days.
        </p>
        <div className="mx-auto max-w-md rounded-xl border border-[#00E5CC]/20 bg-[#00E5CC]/10 px-5 py-4 text-sm text-[#00E5CC]">
          Your message was delivered to {CONTACT_EMAIL}.
          {submittedEmail && (
            <span className="mt-1 block text-slate-300">We will reply to {submittedEmail}.</span>
          )}
        </div>
        <button
          type="button"
          className="mt-8 rounded-full border border-white/20 px-8 py-3 text-sm font-semibold text-white transition hover:border-white/40 hover:bg-white/5"
          onClick={resetForm}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="relative space-y-5">
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-black text-white sm:text-3xl">Send us a message</h2>
        <p className="text-sm leading-relaxed text-slate-400">
          Complete the form below and we will send your inquiry securely to {CONTACT_EMAIL}.
        </p>
      </div>

      {/* Honeypot — hidden from users, catches bots */}
      <label htmlFor="contact-website" className="sr-only" aria-hidden="true">
        Website
      </label>
      <input
        id="contact-website"
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(event) => setHoneypot(event.target.value)}
        className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col sm:col-span-2">
          <label htmlFor="contact-fullName" className="mb-2 text-sm font-medium text-slate-300">
            Full Name <span className="text-[#00E5CC]">*</span>
          </label>
          <input
            id="contact-fullName"
            name="fullName"
            type="text"
            required
            autoComplete="name"
            maxLength={FIELD_LIMITS.fullName}
            value={formData.fullName}
            placeholder="e.g. Amina Hassan"
            className={inputClass(errors.fullName)}
            onChange={(event) => handleFieldChange("fullName", event.target.value)}
            aria-invalid={Boolean(errors.fullName)}
            aria-describedby={errors.fullName ? "contact-fullName-error" : undefined}
          />
          {errors.fullName && (
            <span id="contact-fullName-error" className="mt-1.5 text-xs text-red-400" role="alert">
              {errors.fullName}
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <label htmlFor="contact-email" className="mb-2 text-sm font-medium text-slate-300">
            Email <span className="text-[#00E5CC]">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            maxLength={FIELD_LIMITS.email}
            value={formData.email}
            placeholder="e.g. amina@example.org"
            className={inputClass(errors.email)}
            onChange={(event) => handleFieldChange("email", event.target.value)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "contact-email-error" : undefined}
          />
          {errors.email && (
            <span id="contact-email-error" className="mt-1.5 text-xs text-red-400" role="alert">
              {errors.email}
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <label htmlFor="contact-phone" className="mb-2 text-sm font-medium text-slate-300">
            Phone Number <span className="text-[#00E5CC]">*</span>
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            required
            autoComplete="tel"
            maxLength={FIELD_LIMITS.phone}
            value={formData.phone}
            placeholder="e.g. +255 7XX XXX XXX"
            className={inputClass(errors.phone)}
            onChange={(event) => handleFieldChange("phone", event.target.value)}
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={errors.phone ? "contact-phone-error" : undefined}
          />
          {errors.phone && (
            <span id="contact-phone-error" className="mt-1.5 text-xs text-red-400" role="alert">
              {errors.phone}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col">
        <label htmlFor="contact-subject" className="mb-2 text-sm font-medium text-slate-300">
          Subject <span className="text-[#00E5CC]">*</span>
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          maxLength={FIELD_LIMITS.subject}
          value={formData.subject}
          placeholder="e.g. Membership inquiry"
          className={inputClass(errors.subject)}
          onChange={(event) => handleFieldChange("subject", event.target.value)}
          aria-invalid={Boolean(errors.subject)}
          aria-describedby={errors.subject ? "contact-subject-error" : undefined}
        />
        {errors.subject && (
          <span id="contact-subject-error" className="mt-1.5 text-xs text-red-400" role="alert">
            {errors.subject}
          </span>
        )}
      </div>

      <div className="flex flex-col">
        <label htmlFor="contact-message" className="mb-2 text-sm font-medium text-slate-300">
          Message <span className="text-[#00E5CC]">*</span>
        </label>
        <div className="relative">
          <textarea
            id="contact-message"
            name="message"
            required
            rows={6}
            maxLength={FIELD_LIMITS.message}
            value={formData.message}
            placeholder="Tell us how we can help..."
            className={`${inputClass(errors.message)} resize-y min-h-[140px] pr-16 sm:resize-none`}
            onChange={(event) => handleFieldChange("message", event.target.value)}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? "contact-message-error" : undefined}
          />
          <span className="pointer-events-none absolute bottom-3 right-4 text-xs text-slate-600">
            {formData.message.length} / {FIELD_LIMITS.message}
          </span>
        </div>
        {errors.message && (
          <span id="contact-message-error" className="mt-1.5 text-xs text-red-400" role="alert">
            {errors.message}
          </span>
        )}
      </div>

      {submitError && (
        <div
          className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          role="alert"
          aria-live="assertive"
        >
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full rounded-full py-4 text-center text-sm font-bold transition sm:text-base ${
          isSubmitting
            ? "cursor-not-allowed bg-[#00E5CC]/70 text-[#0B1629]"
            : "bg-[#00E5CC] text-[#0B1629] hover:bg-[#00BFA5] focus:outline-none focus:ring-2 focus:ring-[#00E5CC]/50 focus:ring-offset-2 focus:ring-offset-[#0B1629]"
        }`}
        aria-busy={isSubmitting}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center justify-center gap-2">
            <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" />
              <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            Sending...
          </span>
        ) : (
          "Send message"
        )}
      </button>
    </form>
  );
};

export default ContactForm;
